import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FishingSession } from '../entities/FishingEntities';
import { getActiveSession, endSession } from '../services/FishingService';

interface FishingLogPanelProps {}

const FishingLogPanel: React.FC<FishingLogPanelProps> = () => {
  const [activeSession, setActiveSession] = useState<FishingSession | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadActiveSession();
    
    // Reload session data every 5 seconds to keep stats updated
    const interval = setInterval(loadActiveSession, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const loadActiveSession = async () => {
    try {
      const session = await getActiveSession();
      setActiveSession(session);
    } catch (error) {
      console.error('Error loading active session:', error);
    }
  };

  const handleEndSession = async () => {
    if (!activeSession) return;
    
    try {
      await endSession(activeSession.id);
      setActiveSession(null);
      setIsExpanded(false);
    } catch (error) {
      console.error('Error ending session:', error);
    }
  };

  const handleViewSessions = () => {
    navigate('/sessions');
  };

  const formatDuration = (startTime: number) => {
    const duration = Date.now() - startTime;
    const hours = Math.floor(duration / (1000 * 60 * 60));
    const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  if (!activeSession) {
    return null;
  }

  return (
    <div style={styles.panel}>
      {!isExpanded ? (
        <div 
          style={styles.compact}
          onClick={() => setIsExpanded(true)}
        >
          <div style={styles.compactIcon}>🎣</div>
          
          <div style={styles.compactInfo}>
            <div style={styles.compactTitle}>Aktivt fiskepass</div>
            <div style={styles.compactDetails}>
              <span>{formatDuration(activeSession.startTime)}</span>
              <span>•</span>
              <span>{activeSession.catchCount} fångster</span>
            </div>
          </div>
          
          <div style={styles.expandIcon}>▲</div>
        </div>
      ) : (
        <div style={styles.expanded}>
          <div 
            style={styles.header}
            onClick={() => setIsExpanded(false)}
          >
            <div style={styles.headerLeft}>
              <div style={styles.sessionIcon}>🎣</div>
              <div>
                <div style={styles.sessionTitle}>Aktivt fiskepass</div>
                <div style={styles.sessionTime}>
                  Startad: {new Date(activeSession.startTime).toLocaleTimeString('sv-SE', {hour: '2-digit', minute: '2-digit'})}
                </div>
              </div>
            </div>
            <div style={styles.expandIcon}>▼</div>
          </div>

          <div style={styles.sessionStats}>
            <div style={styles.statItem}>
              <div style={styles.statLabel}>Tid</div>
              <span>{formatDuration(activeSession.startTime)}</span>
            </div>
            
            <div style={styles.statItem}>
              <div style={styles.statLabel}>Fångster</div>
              <span>{activeSession.catchCount}</span>
            </div>

            {activeSession.waterTemp && (
              <div style={styles.statItem}>
                <div style={styles.statLabel}>Vatten</div>
                <span>{activeSession.waterTemp}°C</span>
              </div>
            )}
          </div>

          <div style={styles.actions}>
            <button 
              style={styles.actionButton}
              onClick={handleViewSessions}
            >
              Visa alla pass
            </button>
            
            <button 
              style={{...styles.actionButton, ...styles.endButton}}
              onClick={handleEndSession}
            >
              Avsluta pass
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  panel: {
    position: 'fixed' as const,
    bottom: '70px',
    left: '10px',
    right: '10px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
    border: '1px solid #e0e0e0',
    zIndex: 1000,
  },

  compact: {
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },

  compactIcon: {
    fontSize: '20px',
    marginRight: '12px',
  },

  compactInfo: {
    flex: 1,
  },

  compactTitle: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '2px',
  },

  compactDetails: {
    fontSize: '12px',
    color: '#666',
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },

  expandIcon: {
    fontSize: '12px',
    color: '#666',
  },

  expanded: {
    padding: '16px',
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    cursor: 'pointer',
  },

  headerLeft: {
    display: 'flex',
    alignItems: 'center',
  },

  sessionIcon: {
    fontSize: '24px',
    marginRight: '12px',
  },

  sessionTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#333',
  },

  sessionTime: {
    fontSize: '12px',
    color: '#666',
    marginTop: '2px',
  },

  sessionStats: {
    display: 'flex',
    justifyContent: 'space-around',
    marginBottom: '16px',
    padding: '12px',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
  },

  statItem: {
    textAlign: 'center' as const,
    flex: 1,
  },

  statLabel: {
    fontSize: '11px',
    color: '#666',
    textTransform: 'uppercase' as const,
    marginBottom: '4px',
  },

  actions: {
    display: 'flex',
    gap: '12px',
  },

  actionButton: {
    flex: 1,
    padding: '12px',
    border: '1px solid #007AFF',
    backgroundColor: 'white',
    color: '#007AFF',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },

  endButton: {
    backgroundColor: '#FF3B30',
    color: 'white',
    borderColor: '#FF3B30',
  },
};

export default FishingLogPanel;
